import { GCM_NONCE_LEN, GCM_TAG_LEN } from './bytes';
import { assertVerified, segmentsOf, type Segment } from './envelope';
import { EnvelopeError } from './errors';

import type { VerifiedEnvelope } from './types';

const SEGMENT_INDEX_LEN = 8;
const FINAL_FLAG_LEN = 1;

export async function decryptDocument(
  verified: VerifiedEnvelope,
  dataKey: CryptoKey,
): Promise<Uint8Array> {
  assertVerified(verified);
  assertNonExtractableAesGcmDecryptKey(dataKey);
  const { header, headerBytes, ct } = verified;
  const headerHash = await sha256(headerBytes);
  const plaintext = new Uint8Array(header.plainLen);
  try {
    let written = 0;
    for (const segment of segmentsOf(header, ct)) {
      const segmentPlaintext = await decryptSegment(dataKey, ct, segment, headerHash);
      written = copySegmentInto(plaintext, segmentPlaintext, segment);
    }
    if (written !== header.plainLen) {
      throw new EnvelopeError('length', header.segCount - 1);
    }
    if ((await fileHashOf(plaintext)) !== header.fileHash) {
      throw new EnvelopeError('file_hash');
    }
    return plaintext;
  } catch (err) {
    plaintext.fill(0);
    throw err;
  }
}

async function decryptSegment(
  dataKey: CryptoKey,
  ct: Uint8Array,
  segment: Segment,
  headerHash: Uint8Array,
): Promise<Uint8Array> {
  const sealed = ct.subarray(segment.start, segment.end);
  try {
    const plaintext = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: sealed.subarray(0, GCM_NONCE_LEN),
        additionalData: segmentAad(headerHash, segment),
        tagLength: GCM_TAG_LEN * 8,
      },
      dataKey,
      sealed.subarray(GCM_NONCE_LEN),
    );
    return new Uint8Array(plaintext);
  } catch {
    throw new EnvelopeError('tag', segment.index);
  }
}

function copySegmentInto(
  plaintext: Uint8Array,
  segmentPlaintext: Uint8Array,
  segment: Segment,
): number {
  try {
    const end = segment.plaintextOffset + segmentPlaintext.length;
    if (end > plaintext.length) throw new EnvelopeError('length', segment.index);
    plaintext.set(segmentPlaintext, segment.plaintextOffset);
    return end;
  } finally {
    segmentPlaintext.fill(0);
  }
}

function segmentAad(headerHash: Uint8Array, segment: Segment): Uint8Array {
  const aad = new Uint8Array(headerHash.length + SEGMENT_INDEX_LEN + FINAL_FLAG_LEN);
  aad.set(headerHash, 0);
  new DataView(aad.buffer).setBigUint64(headerHash.length, BigInt(segment.index), false);
  aad[headerHash.length + SEGMENT_INDEX_LEN] = segment.final ? 1 : 0;
  return aad;
}

function assertNonExtractableAesGcmDecryptKey(dataKey: CryptoKey): void {
  const algorithm = dataKey.algorithm as AesKeyAlgorithm;
  if (
    dataKey.extractable ||
    dataKey.type !== 'secret' ||
    algorithm.name !== 'AES-GCM' ||
    algorithm.length !== 256 ||
    !dataKey.usages.includes('decrypt')
  ) {
    throw new TypeError('data key must be a non-extractable AES-256-GCM decrypt key');
  }
}

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
}

async function fileHashOf(plaintext: Uint8Array): Promise<string> {
  const digest = await sha256(plaintext);
  return `sha256:${Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}
